/**
 * Referral-code capture + persistence — the FE half of the user-brings-
 * user program.
 *
 * A code arrives one of two ways:
 *   1. URL param `?ref=CODE` on ANY page (landing, generator, share links,
 *      song pages). Captured to localStorage the moment we see it so it
 *      survives the OAuth redirect / signup wizard / whatever happens
 *      before the user account actually gets created.
 *   2. Manual entry in the "Have an invite code?" field on the register
 *      form (word-of-mouth escape hatch).
 *
 * Both paths converge on the same value in localStorage, which the
 * signup completion handlers (RegisterPage + AuthCallbackPage) read + POST
 * to `POST /:app/referrals/attribute` alongside the freshly-created
 * userId. On success the store is cleared so the code isn't re-used on a
 * later account.
 */

const STORAGE_KEY = 'mixgenie:pendingReferralCode';
// Codes are alphanumeric (module constants: 30-char alphabet, length 6).
// The regex is intentionally lenient — the BE is authoritative — but
// filters out obvious garbage so we don't POST 500-char pastes.
const CODE_SHAPE = /^[A-Z0-9]{4,16}$/;

export function readReferralCode(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return null;
    const clean = v.toUpperCase().trim();
    return CODE_SHAPE.test(clean) ? clean : null;
  } catch {
    return null;
  }
}

export function writeReferralCode(code: string): void {
  const clean = String(code || '').toUpperCase().trim();
  if (!CODE_SHAPE.test(clean)) return;
  try {
    localStorage.setItem(STORAGE_KEY, clean);
  } catch { /* private mode / disabled — silent */ }
}

export function clearReferralCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
}

/**
 * Sweep the current URL for `?ref=CODE` and persist it. Also strips the
 * param from the address bar so page reloads (or shares) don't leak
 * someone else's code. Call this on every mount of the app root so a
 * landing on ANY route captures the code — not just /register.
 */
export function captureFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('ref');
    if (!code) return;
    writeReferralCode(code);
    url.searchParams.delete('ref');
    const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState({}, '', cleaned);
  } catch {
    /* URL parse errors are unrecoverable and non-critical */
  }
}
